// Card 15, the tidal diamond (R548; ui-map-interface.md §17.3.15).
//
// A paper chart marks a point on the water with a magenta diamond and a
// letter, and a table in the margin gives the tidal stream at that point,
// hour by hour. The world clock is this map's tide, and the owner cut the
// margin table: every tier-2 structure wears its own lettered diamond, and
// its own two-row table stands BESIDE it on the water.
//
// The two rows are the clock and the tally a commander plans a siege by:
//
//   1. the VULNERABILITY clock — how long until the object's window
//      opens, or, while it is open, when it closes;
//   2. the SIEGE tally — how many fleets stand on each side.
//
// The second row used to count down to the attack lock. R787 deleted the
// lock: a pledge is taken while the window runs and at no other hour, so
// there is one clock to watch and the tally is the other reading.
//
// There is no day-or-night row and no seaquake row: the owner ruled both
// out, and the season banner already carries the stage.
//
// **THE CLOCK IS DERIVED.** `GameState.windowInSec(hour)` re-derives the
// countdown from the world clock, because a Sieges frame is sent on three
// edges only. The piece rebuilds on `'minute'`, so the table re-labels once
// a world minute and never in a frame. A countdown under an hour reads its
// seconds (`3m 23s`, R2741), so while one stands the piece answers its own
// edge once a world second (`outgrown`).
//
// **THE LETTER IS THE SITE ORDER.** The structures are sorted by site id and
// take `S.letters` in that order, so the letter a commander names is the
// same letter every other commander sees, and it does not move when a
// window resolves.

import { CHART } from '../../../gen/presentation';
import { chart as S } from '../../../gen/strings';
import { diamond } from '../../../render/chart-glyphs';
import { toRoman } from '../../../render/roman';
import { cellCenter } from '../../../world';
import type { FrameInfo } from '../../layer';
import type {
  ChartContext, ChartPack, ChartSinks, ChartTier, Piece, RebuildReason,
} from '../context';
import { stepPx } from '../steps';
import { namedSite } from './beacon-read';
import { CHART_INK, onTheHour, showsAlways } from './shared';
import { leftSpan, leftTicksBySecond } from '../../../locale/span';

/** One structure's diamond and its two rows, already worded. */
interface Station {
  /** The structure's cell centre, in world px. */
  cx: number;
  cy: number;
  /** The letter this station wears, or the empty string past the pool. */
  letter: string;
  /** The two rows of the modern reading. */
  rows: [string, string];
  /** The same two rows in the historical reading. */
  histRows: [string, string];
  /** The establishment line, which the historical reading writes instead. */
  establishment: string;
}

/**
 * The same countdown in whole minutes, as a numeral.
 *
 * The historical reading counts in minutes because the numeral system has
 * no zero: a span under a minute reads `I`, never an empty run.
 */
function romanMinutes(sec: number): string {
  return toRoman(Math.max(1, Math.round(Math.max(0, sec) / 60)));
}

/** Does the shipped table draw every structure, or only the named one? */
const TIDAL_ALWAYS = showsAlways(CHART.pieces.tidal_diamond.show);

export class TidalDiamondPiece implements Piece {
  readonly id = 'tidal_diamond' as const;
  readonly tier: ChartTier = 'screen';
  readonly pack: ChartPack = 'static';
  readonly film = 'hide' as const;
  readonly rebuildOn: readonly RebuildReason[];

  private stations: Station[] = [];
  /** A countdown under an hour stands, so the words move each second. */
  private bySecond = false;
  /** The world second of the last build. */
  private second = -1;

  /**
   * The same `show` flag cards 34 and 46 keep (R569): `"hover"` draws the
   * diamond of the structure the pointer or the selection names, and
   * `"always"` draws one per structure. The owner hid the piece by
   * default on 2026-09-06, and the test states both ways.
   */
  constructor(private readonly always: boolean = TIDAL_ALWAYS) {
    this.rebuildOn = always
      ? ['sieges', 'minute', 'reading']
      : ['sieges', 'minute', 'reading', 'hover', 'selection'];
  }

  /** How many structures wear a diamond now. The piece's test reads it. */
  get count(): number { return this.stations.length; }

  visible(ctx: ChartContext): boolean {
    return this.always || namedSite(ctx) !== 0;
  }

  rebuild(ctx: ChartContext): void {
    const named = this.always ? 0 : namedSite(ctx);
    // The letter is the site order over EVERY structure, so a diamond
    // shown alone keeps the letter it wears among the others.
    this.bySecond = false;
    this.second = Math.floor(ctx.worldClockS);
    const list = [...ctx.state.sieges].sort((a, b) => a.siteId - b.siteId)
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => this.always || s.siteId === named);
    this.stations = list.map(({ s, i }) => {
      const [cx, cy] = cellCenter(s.cell);
      const inSec = ctx.state.windowInSec(s.windowHour);
      const open = inSec < 0;
      // The window's length is a pack figure on the runtime lane (R787),
      // so the closing hour comes from the store and never from a literal.
      const closesAt = ctx.state.windowClosesHour(s.windowHour);
      if (!open && leftTicksBySecond(inSec)) this.bySecond = true;
      return {
        cx, cy,
        letter: S.letters[i] ?? '',
        rows: [
          open
            ? S.tidal_open({ at: onTheHour(closesAt) })
            : S.tidal_opens({ in: leftSpan(inSec) }),
          // The pledge secrecy rule (R794): your own faction's count, and
          // a flag that says whether another faction pledged anything.
          s.enemyPledged
            ? S.tidal_contested({ own: s.ownCommits })
            : S.tidal_quiet({ own: s.ownCommits }),
        ],
        histRows: [
          open
            ? S.tidal_open_historical({ hour: onTheHour(closesAt) })
            : S.tidal_opens_historical({ in: S.tidal_span_historical({ m: romanMinutes(inSec) }) }),
          (s.enemyPledged ? S.tidal_contested_historical : S.tidal_quiet_historical)({
            own: toRoman(s.ownCommits) || String(s.ownCommits),
          }),
        ],
        establishment: S.tidal_establishment({ hour: onTheHour(s.windowHour) }),
      };
    });
  }

  /** A new world second while a countdown under an hour stands (R2741):
   *  one compare. */
  outgrown(ctx: ChartContext): boolean {
    return this.bySecond && Math.floor(ctx.worldClockS) !== this.second;
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    if (this.stations.length === 0) return;
    const t = ctx.cfg.pieces.tidal_diamond;
    const reading = ctx.reading;
    for (const st of this.stations) {
      const dx = st.cx + t.fig.diamond_dx;
      const dy = st.cy + t.fig.diamond_dy;
      if (reading === 'historical') {
        // The old chart engraves the establishment of the port beside the
        // hold and wears no diamond, so the three lines stack from there.
        const r = t.historical;
        const lines = [st.establishment, st.histRows[0], st.histRows[1]];
        // The three lines are one block, so they grow about one pivot.
        const pivot = { x: dx, y: dy };
        for (let i = 0; i < lines.length; i++) {
          ctx.glyphs.word(
            sinks.quads, lines[i], dx, dy + (i - 1) * t.fig.row_h,
            'fell', t.text.main, CHART_INK.pale, r.alpha, { pivot },
          );
        }
        continue;
      }
      const r = t.modern;
      diamond(
        sinks.segments, dx, dy, t.fig.half_w, t.fig.half_h,
        CHART_INK.magenta, t.fig.alpha, t.fig.width,
      );
      // The letter stands inside its drawn diamond, so it takes the size of
      // its step and grows with the water as the diamond does (R1796).
      ctx.glyphs.run(
        sinks.quads, st.letter, dx, dy,
        'mono', stepPx('mono', t.text.main), CHART_INK.magenta, r.alpha, { anchor: 'centre' },
      );
      // The table straddles the diamond's own line: the first row half a
      // row above the centre, the second half a row below. The two rows are
      // one block, so they grow about one pivot.
      const left = dx + t.fig.half_w + t.fig.table_dx;
      const pivot = { x: left, y: dy };
      for (let i = 0; i < st.rows.length; i++) {
        ctx.glyphs.word(
          sinks.quads, st.rows[i], left, dy + (i - 0.5) * t.fig.row_h,
          'mono', t.text.row, CHART_INK.magenta, r.fig.value_alpha, { pivot },
        );
      }
    }
  }
}
